import {
  KoobInputNumber,
  KoobInputLabel,
  KoobInputSelect,
  KoobInputCheckbox,
  KoobInputStars,
} from '@koob/ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import BulkEditorItemContracts from './BulkEditorItemContracts';
import { Field, useField } from 'formik';
import BulkEditorItemRooms from './BulkEditorItemRooms';
import { toBooleanSelectValue } from '../../utils';
import { KoobBadge, KoobId } from '@koob/ui';
import { camelCase } from 'lodash';

const STATE_BADGE_COLORS = {
  available: 'green',
  inProgress: 'blue',
  standBy: 'amber',
};

export default function BulkEditorItem({ item, type }) {
  const { t } = useTranslation('toConnectionRequest');

  const fieldName = useMemo(() => {
    switch (type) {
      case 'hotel':
        return 'toConnectionRequestHotels';
      case 'experience':
        return 'toConnectionRequestExperiences';
      case 'template':
        return 'toConnectionRequestTemplates';
      default:
        return '';
    }
  }, [type]);

  const [{ value: field }, , { setValue: setField }] = useField(
    `request.${fieldName}`,
  );

  const findIndex = field?.findIndex(
    (fieldItem) => String(fieldItem.id) === String(item.id),
  );
  const itemIndex = findIndex >= 0 ? findIndex : null;
  const itemField =
    itemIndex !== null
      ? `request.${fieldName}[${itemIndex}]`
      : `request.${fieldName}.placeholder`;

  const [{ value: itemFieldValue }] = useField(itemField);
  const [{ value: isMarginPercentValue }, , { setValue: setIsMarginPercent }] =
    useField(`${itemField}.isMarginPercent`);
  const [{ value: distributeValue }, , { setValue: setDistribute }] = useField(
    `${itemField}.distribute`,
  );

  const isSelected =
    itemFieldValue?.isSelected === true ||
    itemFieldValue?.isSelected === 'true';

  const addItem = () => {
    if (itemIndex !== null) {
      const newField = [...field];
      newField[itemIndex] = { ...newField[itemIndex], isSelected: true };
      setField(newField);
      return;
    }

    setField([
      ...(field ?? []),
      {
        ...item,
        id: item.id,
        isMarginPercent: null,
        margin: null,
        distribute: null,
        isSelected: true,
        fromModel: false,
      },
    ]);
  };

  const removeItem = () => {
    if (itemIndex === null) {
      return;
    }

    const newField = [...field];
    newField[itemIndex] = { ...newField[itemIndex], isSelected: false };
    setField(newField);
  };

  return (
    <div
      className={[
        'bg-white border border-l-4 rounded-md py-5 px-6',
        isSelected && 'border-l-k-orange',
      ].join(' ')}
    >
      <div className="grid grid-cols-3 items-center">
        <div className="flex space-x-3 items-center">
          <KoobInputCheckbox
            checked={isSelected}
            onCheckedChange={(checked) => {
              checked ? addItem() : removeItem();
            }}
          />

          <div className="flex-shrink-0 bg-gray-50 w-12 h-12 rounded-md overflow-hidden">
            {item.image && (
              <img
                src={item.image}
                alt=""
                className="w-full h-full object-cover border-none"
              />
            )}
          </div>

          <div className="min-w-0">
            <div className="font-medium leading-tight text-gray-700 line-clamp-2 break-words">
              {item.name}
            </div>
            <KoobId id={item.id} />

            <div>
              <KoobInputStars value={item.stars} size="xs" />
            </div>

            {item.description && (
              <div className="text-gray-500 text-xs">{item.description}</div>
            )}
          </div>
        </div>

        <div className="col-span-2 flex justify-end space-x-3 items-center">
          {item.state && (
            <div className="w-28 flex-shrink-0">
              <div className="mb-1.5 text-sm font-medium leading-none text-gray-900">
                {t('experiences:state.title')}
              </div>

              <KoobBadge
                size="md"
                color={STATE_BADGE_COLORS[camelCase(item.state)] ?? 'gray'}
              >
                {t(`experiences:state.${camelCase(item.state)}`, item.state)}
              </KoobBadge>
            </div>
          )}

          <KoobInputSelect
            label={t('bulkEdit.editor.marginType')}
            placeholder={t('bulkEdit.editor.noChange')}
            value={toBooleanSelectValue(isMarginPercentValue)}
            onValueChange={setIsMarginPercent}
            options={[
              { label: t('bulkEdit.editor.noChange'), value: '' },
              { label: t('bulkEdit.editor.marginPercent'), value: true },
              { label: t('bulkEdit.editor.marginFixed'), value: false },
            ]}
            disabled={
              !isSelected ||
              itemFieldValue?.distribute === 'false' ||
              itemFieldValue?.distribute === false
            }
          />

          <KoobInputLabel label={t('bulkEdit.editor.marginValue')}>
            <Field name={`${itemField}.margin`}>
              {({ field, form }) => (
                <KoobInputNumber
                  min={0}
                  disabled={
                    !isSelected ||
                    itemFieldValue?.distribute === 'false' ||
                    itemFieldValue?.distribute === false
                  }
                  name={field.name}
                  value={field.value}
                  onValueChange={(value) =>
                    form.setFieldValue(field.name, value ?? '')
                  }
                  onBlur={field.onBlur}
                />
              )}
            </Field>
          </KoobInputLabel>

          <KoobInputSelect
            label={t('bulkEdit.editor.visibility')}
            placeholder={t('bulkEdit.editor.noChange')}
            value={toBooleanSelectValue(distributeValue)}
            onValueChange={setDistribute}
            options={[
              { label: t('bulkEdit.editor.noChange'), value: '' },
              { label: t('bulkEdit.editor.visible'), value: true },
              { label: t('bulkEdit.editor.invisible'), value: false },
            ]}
            disabled={!isSelected}
          />

          {type === 'hotel' && (
            <div className="flex items-center space-x-3 mt-4">
              <BulkEditorItemRooms
                hotel={item}
                itemField={itemField}
                disabled={
                  !isSelected ||
                  itemFieldValue?.distribute === 'false' ||
                  itemFieldValue?.distribute === false
                }
              />
              <BulkEditorItemContracts
                itemField={itemField}
                hotelId={item.id}
                hotelName={item.name}
                disabled={
                  !isSelected ||
                  itemFieldValue?.distribute === 'false' ||
                  itemFieldValue?.distribute === false
                }
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
